/* eslint-disable @typescript-eslint/naming-convention */
import { v4 as uuidv4 } from 'uuid';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { initialize, change, reset } from 'redux-form';
import moment from 'moment';

import {
  showProgressBar,
  hideProgressBar,
  showLoadingSpinner,
} from '../../actions';
import {
  INVENTORY_TRANSFER_DELETE,
  INVENTORY_ADVANCED_TRANSFER_ALL_TAB_REQUEST,
  INVENTORY_ADVANCED_TRANSFER_TAB_REQUEST,
  INVENTORY_ADVANCED_TRANSFER_REQUEST_EDIT,
  INVENTORY_ADVANCED_TRANSFER_EDIT,
  INVENTORY_ADVANCE_TRANSFER_CLOSE_FORM_DETAIL,
  INVENTORY_ADVANCE_TRANSFER_SUBMIT_SHIPMENT,
  INVENTORY_ADVANCE_TRANSFER_SUBMIT_FULFILLMENT,
  INVENTORY_ADVANCE_TRANSFER_FETCH_NEXT_PAGE,
} from '../../actions/inventory/transfer/constants';
import * as notificationActions from '../../actions/notifications';
import * as loadingSpinnerActions from '../../actions/loadingSpinner';
import * as transferActions from '../../actions/inventory/transfer';
import {
  setAdvancedTransferList,
  setAdvancedTransferFormType,
} from '../../actions/inventory/transfer/advancedTransfer';
import * as actInvAdvTrf from '../../actions/inventory/transfer/advancedTransfer';
import {
  AdvancedTransferDeleteAction,
  AdvancedTransferEditAction,
  AdvancedTransferFetchAllTabAction,
  AdvancedTransferFetchTabAction,
  AdvancedTransferNextPageAction,
  AdvancedTransferRequestEditAction,
} from '../../actions/inventory/transfer/types';
import {
  FILTER_DELETED_OUTLETS,
  NAMESPACE_INGREDIENTS,
  NAMESPACE_ITEMS,
} from '../../actions/inventory/constants';
import {
  setShowConfirmSubmitModal,
  setTransferDetail,
  closeRightColumn,
  onClickRow,
  setTransferType,
} from '../../actions/inventory/transfer';

import {
  deleteAdvancedTransferIngredient,
  deleteAdvancedTransferItem,
  getAdvancedInventoryIngredientTransfer,
  getAdvancedInventoryItemTransfer,
  TransferListResponse,
} from '../../api/inventory/transfer/advancedTransfer';
import { GetListParams } from '../../api/inventory/transfer';
import { getAllOutlets } from '../../api/outlets/getAllOutlets/getAllOutlets';
import * as apiAdvItemTrf from '../../api/inventory/advancedTransfer';
import * as apiAdvIngTrf from '../../api/ingredientInventory/advancedTransfer';
import { UpdateItemTransferRequest } from '../../api/inventory/advancedTransfer/types/requests';
import { UpdateIngredientsTransferRequest } from '../../api/ingredientInventory/advancedTransfer/types/requests';
import * as apiGetFilteredDate from '../../api/get-filtered-date';
import {
  ActionBarTransferTab,
  TransferTab,
} from '../../reducers/inventory/types';
import { getSelectedOutletIdFromLocalStorage } from '../../utils/selectedOutletId';
import { countRedDot } from '../../utils/actionBar';
import { shouldShowTabList } from '../../utils/transferDetail';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapAdvancedTransfer from '../../clevertap/inventory/advancedTransfer';

import { ERROR_MESSAGE_ITEMS_OR_INGREDIENTS_MORE_THAN_50 } from './constants';
import { polling } from './utils/pollingSaga';

import type { Ingredient, Item, Transfer } from '../../types/transfer/Transfer';
import type { TAB_TYPE } from '../../types/actionBars';
import type { TabParam } from './types/transfer/tabParam';

const getUser = (state) => state.moka.user;
const getTabPermissions = (state) =>
  state.inventory.advancedTransfer.tabPermissions;
const getTabData = (state) => state.inventory.advancedTransfer.tabData;
const getAdvancedTransferPermission = (state) =>
  state.inventory.advancedInventorySettings.permissions.advanced_transfer;
const getAllOutletsState = (state) => state.inventory.transfer.allOutlets;

const tabParams: {
  [key in TAB_TYPE]: TabParam;
} = {
  ALL: {
    tab: 'ALL',
    subject: 'ALL',
    status: [],
  },
  APPROVAL: {
    tab: 'APPROVAL',
    status: ['CREATED'],
    subject: 'SENDER',
  },
  SHIPMENT: {
    tab: 'SHIPMENT',
    status: ['APPROVED', 'PARTIAL'],
    subject: 'SENDER',
  },
  FULFILLMENT: {
    tab: 'FULFILLMENT',
    status: ['PARTIAL'],
    subject: 'RECEIVER',
  },
  REJECTED: {
    tab: 'REJECTED',
    status: ['REJECTED', 'REVISE_NEEDED'],
    subject: 'ALL',
  },
  COMPLETED: {
    tab: 'COMPLETED',
    status: ['COMPLETED'],
    subject: 'ALL',
  },
};

function getFetchTransferListAPI(namespace: string) {
  const isItem = namespace === NAMESPACE_ITEMS;

  return isItem
    ? getAdvancedInventoryItemTransfer
    : getAdvancedInventoryIngredientTransfer;
}

function* initIngredientForm(transfer: Transfer) {
  const ingredients = transfer.items as Ingredient[];

  yield put(
    initialize('transferItem', {
      fromOutlet: transfer.from_outlet_id.toString(),
      toOutlet: transfer.to_outlet_id.toString(),
      transfers: ingredients.map((ingredient) => ({
        ingredientId: ingredient.ingredient_id,
        ingredientName: ingredient.ingredient_name,
        unitId: ingredient.unit_id,
        unitName: ingredient.unit_name,
        quantity: ingredient.requested_quantity,
        inStock: ingredient.in_stock || 0,
        type: ingredient.type,
      })),
    })
  );
}

function* initItemForm(transfer: Transfer) {
  const items = transfer.items as Item[];

  yield put(
    initialize('transferItem', {
      fromOutlet: transfer.from_outlet_id.toString(),
      toOutlet: transfer.to_outlet_id.toString(),
      transfers: items.map((item) => ({
        isItem: item.item_variants?.length === 1,
        itemId: item.item_id,
        itemName: item.item_name,
        itemVariantId: item.item_variant_id,
        itemVariantName: item.item_variant_name,
        quantity: item.requested_quantity,
        inStock: item.in_stock || 0,
      })),
    })
  );
}

function* rerenderAllTabList(namespace: string) {
  const resGetFilteredDate = yield call(apiGetFilteredDate.getFilteredDate);
  const startDate = moment(resGetFilteredDate.data.start_date);
  const endDate = moment(resGetFilteredDate.data.end_date);

  const outletIds = getSelectedOutletIdFromLocalStorage();
  const outletId = outletIds.length > 1 ? null : outletIds[0];

  yield put(
    actInvAdvTrf.fetchAllTabTransferList(
      namespace,
      {
        start: startDate.startOf('day').format(),
        end: endDate.endOf('day').format(),
      },
      outletId
    )
  );
}

function filterDataResult(
  transfers: Transfer[],
  tabType?: TAB_TYPE
): Transfer[] {
  if (tabType === 'FULFILLMENT') {
    return transfers.filter(
      (transfer) => parseFloat(transfer.shipped_percentage) !== 0
    );
  }

  return transfers;
}

export function* removeTransferItem(transferId: number) {
  const currentTabTransfer: TransferTab = yield select(
    (state: any) => state.inventory.advancedTransfer.tabData
  );

  const parsedCurrentTabTransfer = JSON.parse(
    JSON.stringify(currentTabTransfer)
  );
  const updatedTabTransfer = { ...parsedCurrentTabTransfer };

  (['ALL', 'APPROVAL', 'REJECTED'] as TAB_TYPE[]).forEach(
    (tabType: TAB_TYPE) => {
      const currentRedDot = updatedTabTransfer[tabType].redDot;
      const currentList = updatedTabTransfer[tabType].data;
      const updatedList = currentList.filter(
        (transfer) => transfer.id !== transferId
      );
      const isTranferListChange = currentList.length !== updatedList.length;
      const shouldChangeRedDot =
        isTranferListChange && ['APPROVAL', 'REJECTED'].includes(tabType);
      const updatedRedDot = shouldChangeRedDot
        ? currentRedDot - 1
        : currentRedDot;

      updatedTabTransfer[tabType].data = updatedList;
      updatedTabTransfer[tabType].redDot = updatedRedDot;
    }
  );

  yield put(setAdvancedTransferList(updatedTabTransfer));
}

export function* deleteTransfer(request: AdvancedTransferDeleteAction) {
  const { transferId, namespace } = request.payload!;

  try {
    const isItem = namespace === NAMESPACE_ITEMS;
    const deleteApi = isItem
      ? deleteAdvancedTransferItem
      : deleteAdvancedTransferIngredient;

    yield put(loadingSpinnerActions.showPageLoadingSpinner(true));
    const { data: resDelete } = yield call(deleteApi, transferId);
    yield call(removeTransferItem, transferId);

    const tabData = yield select(getTabData);
    const isAdvancedTransfer = yield select(getAdvancedTransferPermission);

    if (!tabData.REJECTED.redDot && !isAdvancedTransfer) {
      yield put(setTransferType('ALL'));
    }

    yield put(transferActions.closeRightColumn());
    yield put(notificationActions.success(resDelete.data.message));
    yield put(transferActions.setTransferDetail(null));
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(notificationActions.error(e.message));
  } finally {
    yield put(loadingSpinnerActions.showPageLoadingSpinner(false));
  }
}

export function mapitemListWithOutlets(
  list: Transfer[],
  mappedOutletsById: any
) {
  return list.map((item) => {
    const fromOutletName = mappedOutletsById[item.from_outlet_id]?.name;
    const toOutletName = mappedOutletsById[item.to_outlet_id]?.name;

    return {
      ...item,
      from_outlet_name: fromOutletName,
      to_outlet_name: toOutletName,
    };
  });
}

export function* mapTransferListWithOutlets(lists: Transfer[][]) {
  const allOutlets = yield select(getAllOutletsState);
  const mappedOutletsById = allOutlets.reduce((obj, outlet) => {
    return {
      ...obj,
      [outlet.id]: outlet,
    };
  }, {});

  return lists.map((list) => mapitemListWithOutlets(list, mappedOutletsById));
}

export function* mapTransferWithOutlets(transfers: Transfer[]) {
  const allOutlets = yield select(getAllOutletsState);

  const mappedOutletsById = allOutlets.reduce((obj, outlet) => {
    return {
      ...obj,
      [outlet.id]: outlet,
    };
  }, {});

  const result = mapitemListWithOutlets(transfers, mappedOutletsById);
  return result;
}

export function* fetchAdvancedTransferList(
  tab: string,
  namespace: string,
  outletId: string,
  params: GetListParams
) {
  const outletIds = yield select((state: any) =>
    state.moka.user.outlets.map(({ id }) => id)
  );
  const api = getFetchTransferListAPI(namespace);
  const result: TransferListResponse = yield call(api, params!, {
    outletIds,
    outletId,
  });
  return {
    ...result,
    tab,
  };
}

export function* advancedTransferRequestEdit(
  request: AdvancedTransferRequestEditAction
) {
  const { namespace, transfer } = request.payload!;

  const { data: outlets } = yield call(getAllOutlets, FILTER_DELETED_OUTLETS);
  yield put(transferActions.setValidOutlets(outlets));

  if (namespace === NAMESPACE_ITEMS) {
    yield call(initItemForm, transfer);
  } else if (namespace === NAMESPACE_INGREDIENTS) {
    yield call(initIngredientForm, transfer);
  }

  yield put(setAdvancedTransferFormType('UPDATE'));
}

export function* updateItems(id: number, transferItem: any) {
  const requestId = uuidv4();
  const requestBody: UpdateItemTransferRequest = {
    request_id: requestId,
    from_outlet_id: Number(transferItem.fromOutlet),
    to_outlet_id: Number(transferItem.toOutlet),
    note: transferItem.notes ?? '',
    items: transferItem.transfers.map((val) => ({
      item_id: val.itemId,
      item_name: val.itemName,
      item_variant_id: val.itemVariantId,
      item_variant_name: val.itemVariantName ?? val.itemName,
      quantity: val.quantity,
    })),
  };
  const result = yield call(
    apiAdvItemTrf.putUpdateItemsTransfer,
    id,
    requestBody
  );
  return result;
}

export function* updateIngredients(id: number, transferItem: any) {
  const requestId = uuidv4();
  const requestBody: UpdateIngredientsTransferRequest = {
    request_id: requestId,
    from_outlet_id: Number(transferItem.fromOutlet),
    to_outlet_id: Number(transferItem.toOutlet),
    note: transferItem.notes ?? '',
    ingredients: transferItem.transfers.map((val) => ({
      ingredient_id: val.ingredientId,
      ingredient_name: val.ingredientName,
      unit_id: val.unitId,
      unit_name: val.unitName,
      quantity: val.quantity,
    })),
  };
  const result = yield call(
    apiAdvIngTrf.putUpdateIngredientsTransfer,
    id,
    requestBody
  );
  return result;
}

export function* advancedTransferUpdateItemList(transfer: Transfer) {
  const currentTabTransfer: TransferTab = yield select(
    (state: any) => state.inventory.advancedTransfer.tabData
  );
  const allTransferList = currentTabTransfer.ALL?.data;
  const updatedTransferList = allTransferList?.map((currentTranfer) => {
    if (currentTranfer.id === transfer.id) {
      return transfer;
    }
    return currentTranfer;
  });

  const transformedTransfers = yield call(
    mapTransferWithOutlets,
    updatedTransferList!
  );

  const tabData: TransferTab = {
    ALL: {
      ...currentTabTransfer.ALL,
      data: transformedTransfers,
    } as ActionBarTransferTab,
  };
  yield put(setAdvancedTransferList(tabData));

  const updatedTransfer = transformedTransfers.find(
    (data) => data.id === transfer.id
  );
  return updatedTransfer;
}

export function* advancedTransferEdit(request: AdvancedTransferEditAction) {
  const { namespace, id, transferItem } = request.payload!;
  const isItem = namespace === NAMESPACE_ITEMS;
  const apiUpdate = isItem ? updateItems : updateIngredients;

  try {
    if (transferItem.transfers.length > 50) {
      throw new Error(ERROR_MESSAGE_ITEMS_OR_INGREDIENTS_MORE_THAN_50);
    }

    yield put(showProgressBar());
    const result = yield call(apiUpdate, id, transferItem);
    const updatedTransfer = yield call(advancedTransferUpdateItemList, result);

    yield put(setTransferType('ALL'));
    yield call(rerenderAllTabList, namespace);

    yield put(onClickRow(updatedTransfer, namespace));
    yield put(notificationActions.success(result.message));
    yield put(reset('transferItem'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(notificationActions.error(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(setShowConfirmSubmitModal(false));
  }
}

export function* nextPage(request: AdvancedTransferNextPageAction) {
  try {
    const { namespace, outletId, tabType } = request.payload!;
    const hasNextPageTabs: TAB_TYPE[] = ['ALL', 'COMPLETED'];

    if (!hasNextPageTabs.includes(tabType)) {
      return;
    }

    const currentTabTransfer: TransferTab = yield select(
      (state: any) => state.inventory.advancedTransfer.tabData
    );
    const selectedTabTransfer = currentTabTransfer[tabType];
    const { paging } = selectedTabTransfer!;

    if (!paging.end) {
      return;
    }

    const isLastPage = selectedTabTransfer!.data.length < paging.per_page;
    if (isLastPage) {
      return;
    }

    const tabParam: any = tabParams[tabType];
    const fetchedData = yield call(
      fetchAdvancedTransferList,
      tabType,
      namespace,
      outletId,
      {
        start: paging.start,
        end: paging.end,
        ...tabParam,
      }
    );

    const fetchedDataWithOutlets = yield call(
      mapTransferWithOutlets,
      fetchedData.data
    );

    const updatedTabData: TransferTab = {
      [tabParam.tab]: {
        ...selectedTabTransfer,
        ...fetchedData,
        data: selectedTabTransfer?.data.concat(
          filterDataResult(fetchedDataWithOutlets, tabType)
        ),
      },
    };

    yield put(setAdvancedTransferList(updatedTabData));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(notificationActions.error(error.message));
  }
}

export function* tabRequest(request: AdvancedTransferFetchTabAction) {
  try {
    const { namespace, outletId, params, tabType } = request.payload!;

    const tabPermissions = yield select(getTabPermissions);
    const hasTabAccess = shouldShowTabList(tabPermissions, tabType);

    if (!hasTabAccess) {
      return;
    }

    yield put(loadingSpinnerActions.showPageLoadingSpinner(true));
    const tabParam = tabParams[tabType];

    const showDateFilter = ['ALL', 'COMPLETED'].includes(tabType);
    const apiParams: any = showDateFilter ? params : {};
    apiParams.keyword = params?.keyword;

    const result = yield call(
      fetchAdvancedTransferList,
      tabParam.tab,
      namespace,
      outletId,
      {
        status: tabParam.status,
        subject: tabParam.subject,
        ...apiParams,
      }
    );

    const dataResult = yield call(mapTransferWithOutlets, result.data);

    const currentTabTransfer: TransferTab = yield select(
      (state: any) => state.inventory.advancedTransfer.tabData
    );
    const tabData: TransferTab = {
      [tabParam.tab]: {
        ...currentTabTransfer[result.tab],
        ...result,
        data: filterDataResult(dataResult, result.tab),
      },
    };

    yield put(setAdvancedTransferList(tabData));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(notificationActions.error(error.message));
  } finally {
    yield put(loadingSpinnerActions.showPageLoadingSpinner(false));
  }
}

export function* allTabRequest(request: AdvancedTransferFetchAllTabAction) {
  const { namespace, outletId, params } = request.payload!;

  try {
    yield put(loadingSpinnerActions.showPageLoadingSpinner(true));

    const tabPermissions = yield select(getTabPermissions);
    const allTabParams = Object.values(tabParams);
    const filteredTabParams = allTabParams.filter((tabParam) =>
      shouldShowTabList(tabPermissions, tabParam.tab)
    );
    const results = yield all(
      filteredTabParams.map((tabParam) => {
        const showDateFilter = ['ALL', 'COMPLETED'].includes(tabParam.tab);
        const apiParams = showDateFilter ? params : ({} as typeof params);

        return call(
          // I don't find any suitable fix, temporary cast as any.
          fetchAdvancedTransferList as any,
          tabParam.tab,
          namespace,
          outletId,
          {
            subject: tabParam.subject,
            status: tabParam.status,
            ...apiParams,
          }
        );
      })
    );

    const dataResults = yield call(
      mapTransferListWithOutlets,
      results.map((result) => result.data)
    );

    const tabData: TransferTab = results.reduce((obj, result, index) => {
      const filteredData = filterDataResult(dataResults[index], result.tab);

      return {
        ...obj,
        [result.tab]: {
          ...result,
          data: filteredData,
          redDot: countRedDot(filteredData, result.tab),
        },
      };
    }, {});

    yield put(setAdvancedTransferList(tabData));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(notificationActions.error(error.message));
  } finally {
    yield put(loadingSpinnerActions.showPageLoadingSpinner(false));
  }
}

export function* closeFormDetail() {
  yield put(closeRightColumn());
  yield put(setTransferDetail(null));
}

export function transformShipmentParams(request, requestId) {
  const { namespace, rawParams, type } = request.payload;
  const { items } = rawParams;

  const isStop = type === 'STOP';

  let params;

  if (namespace === NAMESPACE_ITEMS) {
    const filteredItems = items.filter((item) => {
      const hasRemainingQuantity = item.remaining_quantity > 0;
      const hasShip = item.ship > 0;
      const hasNotShip = item.not_ship > 0;

      return hasRemainingQuantity && (isStop || hasShip || hasNotShip);
    });

    params = {
      request_id: requestId,
      item_transfer_id: rawParams.transfer_id,
      items: filteredItems.map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        item_variant_id: item.item_variant_id,
        item_variant_name: item.item_variant_name,
        shipped_quantity: isStop ? 0 : Number(item.ship || 0),
        unshipped_quantity: isStop
          ? item.remaining_quantity
          : Number(item.not_ship || 0),
      })),
      reason: rawParams.reason,
    };
  }

  if (namespace === NAMESPACE_INGREDIENTS) {
    const filteredItems = items.filter((item) => {
      const hasRemainingQuantity = item.remaining_quantity > 0;
      const hasShip = item.ship > 0;
      const hasNotShip = item.not_ship > 0;

      return hasRemainingQuantity && (isStop || hasShip || hasNotShip);
    });

    params = {
      request_id: requestId,
      ingredient_transfer_id: rawParams.transfer_id,
      ingredients: filteredItems.map((item) => ({
        ingredient_id: item.ingredient_id,
        ingredient_name: item.ingredient_name,
        unit_id: item.unit_id,
        unit_name: item.unit_name,
        shipped_quantity: isStop ? 0 : Number(item.ship || 0),
        unshipped_quantity: isStop
          ? item.remaining_quantity
          : Number(item.not_ship || 0),
      })),
      reason: rawParams.reason,
    };
  }

  return params;
}

export function* onSubmitShipment(request) {
  const { namespace } = request.payload;

  const requestId = uuidv4();
  const requestHeadersConfigs = {
    headers: {
      'x-request-uuid': requestId,
    },
  };
  const newParams = transformShipmentParams(request, requestId);

  try {
    yield put(showLoadingSpinner(true));
    if (namespace === NAMESPACE_ITEMS) {
      yield call(
        polling,
        apiAdvItemTrf.submitShipmentItem,
        newParams,
        requestHeadersConfigs
      );
    } else {
      yield call(
        polling,
        apiAdvIngTrf.submitShipmentIngredient,
        newParams,
        requestHeadersConfigs
      );
    }
    yield call(rerenderAllTabList, namespace);

    const tabData = yield select(getTabData);
    const isAdvancedTransfer = yield select(getAdvancedTransferPermission);

    if (!tabData.SHIPMENT.redDot && !isAdvancedTransfer) {
      yield put(setTransferType('ALL'));
    }

    yield put(change('shipmentForm', 'stopShipment', false));
    yield call(closeFormDetail);

    const user = yield select(getUser);
    clevertapAdvancedTransfer.advancedTransferShipment(user.email);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(notificationActions.error(error.message));
    yield call(rerenderAllTabList, namespace);
    yield call(closeFormDetail);
  } finally {
    yield put(showLoadingSpinner(false));
  }
}

export function transformFulfillmentParams(request, requestId) {
  const { namespace, rawParams } = request.payload;
  const { items } = rawParams;

  let params;

  const filteredItems = items.filter(
    (item) => !!item.fulfill || !!item.unfulfill
  );

  if (namespace === NAMESPACE_ITEMS) {
    params = {
      request_id: requestId,
      shipment_id: rawParams.shipment_id,
      item_transfer_id: rawParams.transfer_id,
      items: filteredItems.map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        item_variant_id: item.item_variant_id,
        item_variant_name: item.item_variant_name,
        fulfilled_quantity: parseFloat(item.fulfill),
        unfulfilled_quantity: parseFloat(item.unfulfill),
      })),
      reason: rawParams.reason,
    };
  }

  if (namespace === NAMESPACE_INGREDIENTS) {
    params = {
      request_id: requestId,
      shipment_id: rawParams.shipment_id,
      ingredient_transfer_id: rawParams.transfer_id,
      ingredients: filteredItems.map((item) => ({
        ingredient_id: item.ingredient_id,
        ingredient_name: item.ingredient_name,
        unit_id: item.unit_id,
        unit_name: item.unit_name,
        fulfilled_quantity: item.fulfill,
        unfulfilled_quantity: item.unfulfill,
      })),
      reason: rawParams.reason,
    };
  }

  return params;
}

export function* onSubmitFulfillment(request) {
  const { namespace, isLastFulfill } = request.payload;

  const requestId = uuidv4();
  const requestHeadersConfigs = {
    headers: {
      'x-request-uuid': requestId,
    },
  };
  const newParams = transformFulfillmentParams(request, requestId);

  try {
    yield put(showLoadingSpinner(true));
    if (namespace === NAMESPACE_ITEMS) {
      yield call(
        polling,
        apiAdvItemTrf.submitFulfillmentItem,
        newParams,
        requestHeadersConfigs
      );
    } else {
      yield call(
        polling,
        apiAdvIngTrf.submitFulfillmentIngredient,
        newParams,
        requestHeadersConfigs
      );
    }

    yield call(rerenderAllTabList, namespace);

    const tabData = yield select(getTabData);
    const isAdvancedTransfer = yield select(getAdvancedTransferPermission);

    if (!tabData.FULFILLMENT.redDot && !isAdvancedTransfer) {
      yield put(setTransferType('ALL'));
    }

    yield call(closeFormDetail);

    const user = yield select(getUser);
    if (isLastFulfill) {
      clevertapAdvancedTransfer.completeAdvancedFulfillment(user.email);
    } else {
      clevertapAdvancedTransfer.advancedTransferFulfill(user.email);
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(notificationActions.error(error.message));
    yield call(rerenderAllTabList, namespace);
    yield call(closeFormDetail);
  } finally {
    yield put(showLoadingSpinner(false));
  }
}

export default function* saga() {
  yield takeLatest(INVENTORY_TRANSFER_DELETE, deleteTransfer);
  yield takeLatest(INVENTORY_ADVANCED_TRANSFER_ALL_TAB_REQUEST, allTabRequest);
  yield takeLatest(INVENTORY_ADVANCED_TRANSFER_TAB_REQUEST, tabRequest);
  yield takeLatest(
    INVENTORY_ADVANCED_TRANSFER_REQUEST_EDIT,
    advancedTransferRequestEdit
  );
  yield takeLatest(INVENTORY_ADVANCED_TRANSFER_EDIT, advancedTransferEdit);
  yield takeLatest(
    INVENTORY_ADVANCE_TRANSFER_CLOSE_FORM_DETAIL,
    closeFormDetail
  );
  yield takeLatest(
    INVENTORY_ADVANCE_TRANSFER_SUBMIT_SHIPMENT,
    onSubmitShipment
  );
  yield takeLatest(
    INVENTORY_ADVANCE_TRANSFER_SUBMIT_FULFILLMENT,
    onSubmitFulfillment
  );
  yield takeLatest(INVENTORY_ADVANCE_TRANSFER_FETCH_NEXT_PAGE, nextPage);
}
